import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AlertScrollArea from "./AlertScrollArea";

export default function AlertActionsDropdown({
  items = [],
  disabled = false,
  ariaLabel,
  toggleClassName = "",
  children,
}) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const labelsKey = items.map((item) => String(item.label || "")).join("\u0000");

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const updateMenuPosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;

    const menuHeight = Math.min((items.length * 40) + 20, 280);
    const needsScroll = ((items.length * 36) + 20) > 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight + 12 && rect.top > spaceBelow;
    const computedStyle = window.getComputedStyle(rootRef.current);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    let longestLabelWidth = 0;
    if (context) {
      context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
      labelsKey.split("\u0000").forEach((label) => {
        longestLabelWidth = Math.max(longestLabelWidth, context.measureText(label).width);
      });
    }
    const contentWidth = Math.ceil(longestLabelWidth) + 42 + (needsScroll ? 21 : 0);
    const menuWidth = Math.min(Math.max(rect.width, contentWidth), window.innerWidth - 16);
    setMenuPosition({
      left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
      top: openUpward ? Math.max(8, rect.top - menuHeight - 12) : rect.bottom + 12,
      width: menuWidth,
    });
  }, [items.length, labelsKey]);

  useLayoutEffect(() => {
    if (open) updateMenuPosition();
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  return (
    <div ref={rootRef} className={`dropdown crm-alert-actions-dropdown ${open ? "is-open" : ""}`.trim()} onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className={`btn dropdown-toggle ${toggleClassName}`.trim()}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        {children}
      </button>
      {open && !disabled && menuPosition ? createPortal(
        <div ref={menuRef} className="crm-alert-dropdown__menu" role="menu" aria-label={ariaLabel} style={menuPosition}>
          <AlertScrollArea viewportClassName="crm-alert-dropdown__scroll">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className="crm-alert-dropdown__option"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect?.();
                }}
              >
                {item.label}
              </button>
            ))}
          </AlertScrollArea>
        </div>,
        document.body
      ) : null}
    </div>
  );
}
